import { FileDownloadIcon, MailIcon, TablePager } from '@fukuri-admins/ui/common';
import { ActionButton } from '~/components/common/ActionButton';
import { ContentsHeader } from '~/components/common/ContentsHeader';
import MessageModal from '~/components/common/MessageModal';
import Shimmer from '~/components/Shimmer';
import { BulkSendReminderModal } from '~/components/User/BulkSendReminderModal';
import { PendingUserTableContent } from '../PendingUserTableContent';
import styles from './styles.module.scss';

type PendingUserTableContentProps = React.ComponentProps<typeof PendingUserTableContent>;
type TablePagerProps = React.ComponentProps<typeof TablePager>;
type BulkSendReminderModalProps = React.ComponentProps<typeof BulkSendReminderModal>;
type MessageModalProps = React.ComponentProps<typeof MessageModal>;
type Props = {
  isLoading: boolean;
  errMsg: string;
  filteredUsers: PendingUserTableContentProps['rows'];
  currentPage: TablePagerProps['currentPage'];
  totalPage: TablePagerProps['totalPage'];
  totalCount: TablePagerProps['totalCount'];
  totalPendingCount: BulkSendReminderModalProps['totalCount'];
  isBulkSendReminderModalOpen: BulkSendReminderModalProps['isModalOpen'];
  isErrorMessageModalOpen: MessageModalProps['show'];
  isCompleteModalOpen: MessageModalProps['show'];
  handleOutputCsv: () => Promise<void>;
  setIsBulkSendReminderModalOpen: (open: boolean) => void;
  setIsErrorMessageModalOpen: (open: boolean) => void;
  setIsCompleteModalOpen: (open: boolean) => void;
  handleHeaderSort: PendingUserTableContentProps['onSort'];
  handlePageChange: TablePagerProps['onPageChange'];
  handleBulkSendReminder: BulkSendReminderModalProps['action'];
  disableRowNavigation: PendingUserTableContentProps['disableRowNavigation'];
};

export const PendingUserPresentation: React.FC<Props> = ({
  isLoading,
  errMsg,
  filteredUsers,
  currentPage,
  totalPage,
  totalCount,
  totalPendingCount,
  isBulkSendReminderModalOpen,
  isErrorMessageModalOpen,
  isCompleteModalOpen,
  handlePageChange,
  handleBulkSendReminder,
  handleOutputCsv,
  handleHeaderSort,
  setIsBulkSendReminderModalOpen,
  setIsErrorMessageModalOpen,
  setIsCompleteModalOpen,
  disableRowNavigation,
}) => {
  return (
    <div className={styles.Root}>
      <div className={styles.container}>
        <ContentsHeader
          title='初回ログイン未完了者'
          description='以下より、初回ログイン未完了者の一覧を確認できます。'
          actions={
            <div className={styles.Root__Header__Right}>
              <div className={styles.Root__Header__Right__Content}>
                <p className={styles.Root__Header__Right__Content__Title}>初回ログイン未完了率</p>
                <div className={styles.Root__Header__Right__Content__Rate}>
                  <span className={styles.Root__Header__Right__Content__Rate__Numerator}>
                    {totalPendingCount}
                  </span>
                  <span className={styles.Root__Header__Right__Content__Rate__Denominator}>
                    {' '}
                    / {totalCount}名
                  </span>
                </div>
              </div>
              <ActionButton
                className={styles.Root__Header__Right__Content__Button}
                content='再招待メールを一斉送信'
                size='small'
                type='whiteFill'
                icon={<MailIcon />}
                iconPosition='left'
                action={() => setIsBulkSendReminderModalOpen(true)}
              />
              <ActionButton
                className={styles.Root__Header__Right__Content__Button}
                content='CSVダウンロード'
                size='small'
                type='whiteFill'
                icon={<FileDownloadIcon />}
                iconPosition='left'
                action={handleOutputCsv}
              />
            </div>
          }
        />
        {isLoading ? (
          <Shimmer />
        ) : (
          <div>
            <div className={styles.tableWrapper}>
              <PendingUserTableContent
                rows={filteredUsers}
                onSort={handleHeaderSort}
                disableRowNavigation={disableRowNavigation}
              />
            </div>
            <TablePager
              currentPage={currentPage}
              totalPage={totalPage}
              totalCount={totalPendingCount}
              onPageChange={handlePageChange}
              itemCount={filteredUsers.length}
            />
            <BulkSendReminderModal
              totalCount={totalPendingCount}
              isModalOpen={isBulkSendReminderModalOpen}
              onClose={() => setIsBulkSendReminderModalOpen(false)}
              action={handleBulkSendReminder}
            />
            <MessageModal
              title='エラー'
              description={`エラーが発生しました。\n\n<<エラーメッセージ>>\n${errMsg}`}
              show={isErrorMessageModalOpen}
              onClose={() => setIsErrorMessageModalOpen(false)}
            />
            <MessageModal
              title='送信完了'
              description={'メールの送信を受け付けました。'}
              show={isCompleteModalOpen}
              onClose={() => setIsCompleteModalOpen(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
